import { GzEnumListWebComponent } from '../../../../ui/gzEnumList';
import { html, TemplateResult, PropertyValues } from 'lit';
import { query, property, state } from 'lit/decorators.js';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { TagTypes } from '../../../types/tag-types';
import { SelectedOption } from '../../../../ui/gzEnumList/types';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../../utils/htmlElement.utils';
import { ExcludedTagTypes, ExtendedTagEnumListItem } from '../types';

export class GzTagsEnumListWebComponent extends GzEnumListWebComponent {
  @property({ type: Array, attribute: true, reflect: true })
  filterOptions: ExtendedTagEnumListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  emptyFilterString;

  /**
   * Get only automatic tags from all filter options
   * @returns {ExtendedTagEnumListItem[]}
   */
  public get filterOptionsAutomatic(): ExtendedTagEnumListItem[] {
    return this.filterOptions.filter(option => option.additionalData?.type === TagTypes.TAG_TYPE_AUTOMATIC);
  }

  /**
   * Get only custom tags from all filter options
   * @returns {ExtendedTagEnumListItem[]}
   */
  public get filterOptionsCustom(): ExtendedTagEnumListItem[] {
    return this.filterOptions.filter(option => option.additionalData?.type === TagTypes.TAG_TYPE_CUSTOM);
  }

  public get enabledAutoCheckboxes(): GzCheckboxWebComponent[] {
    const autoFilterOptionsValues = this.filterOptionsAutomatic.map(option => option.value);
    return this.enabledCheckboxes.filter((checkbox: GzCheckboxWebComponent) =>
      autoFilterOptionsValues.includes(checkbox.value)
    );
  }

  public get enabledCustomCheckboxes(): GzCheckboxWebComponent[] {
    const customFilterOptionsValues = this.filterOptionsCustom.map(option => option.value);
    return this.enabledCheckboxes.filter((checkbox: GzCheckboxWebComponent) =>
      customFilterOptionsValues.includes(checkbox.value)
    );
  }

  @query('#allAutoTags')
  private readonly allAutoTags!: HTMLElement;

  @query('#allCustomTags')
  private readonly allCustomTags!: HTMLElement;

  @property({ type: String, attribute: true, reflect: true })
  allAutoTagsLabel = '';

  @property({ type: String, attribute: true, reflect: true })
  allCustomTagsLabel = '';

  @state()
  private isSelectAllAutoChecked = false;

  @state()
  private isSelectAllCustomChecked = false;

  /**
   * Overwritten
   * @returns {TemplateResult | undefined}
   * @protected
   */
  protected renderSelectAllItemsCheckbox(): TemplateResult | undefined {
    if (!this.canSelectAll) {
      return;
    }

    return html`
      <div part="autoBtn" id="allAutoTags" class="option option-all">
        <gz-checkbox
          class="full-checkbox"
          value="allAuto"
          label="${this.allAutoTagsLabel}"
          ?checked="${this.isSelectAllAutoChecked}"
          @change="${this.toggleAllAutoCheckboxes}"
        ></gz-checkbox>
      </div>
      <div part="customBtn" id="allCustomTags" class="option option-all">
        <gz-checkbox
          class="full-checkbox"
          value="allCustom"
          label="${this.allCustomTagsLabel}"
          ?checked="${this.isSelectAllCustomChecked}"
          @change="${this.toggleAllCustomCheckboxes}"
        ></gz-checkbox>
      </div>
    `;
  }

  private setIsSelectAllAutoChecked(value: boolean) {
    this.isSelectAllAutoChecked = value;
  }

  private setIsSelectAllCustomChecked(value: boolean) {
    this.isSelectAllCustomChecked = value;
  }

  private toggleAllAutoCheckboxes(event) {
    this.setIsSelectAllAutoChecked(!this.isSelectAllAutoChecked);

    this.enabledAutoCheckboxes.forEach((checkbox: GzCheckboxWebComponent) => {
      checkbox.checked = this.isSelectAllAutoChecked;

      this.filterOptionsAutomatic.map((item: ExtendedTagEnumListItem) =>
        this.updateDisplayedOptionsState(item, this.isSelectAllAutoChecked)
      );
    });

    this.emitFilterOptionToggledEvent();
  }

  private toggleAllCustomCheckboxes(event) {
    this.setIsSelectAllCustomChecked(!this.isSelectAllCustomChecked);

    this.enabledCustomCheckboxes.forEach((checkbox: GzCheckboxWebComponent) => {
      checkbox.checked = this.isSelectAllCustomChecked;

      this.filterOptionsCustom.map((item: ExtendedTagEnumListItem) =>
        this.updateDisplayedOptionsState(item, this.isSelectAllCustomChecked)
      );
    });

    this.emitFilterOptionToggledEvent();
  }

  /**
   * Overwritten
   * @param {string} search
   * @protected
   */
  protected handleAllValueSearch(search: string) {
    /**
     * On input change && if allCheckboxes option exists:
     * 1. if search string => hide 'ALL' checkboxes
     * 2. no search string, i.e. the input was cleared => display 'ALL' checkboxes
     */
    if (!this.allAutoTags && !this.allCustomTags) {
      return;
    }

    if (search) {
      elementClassListAdd(this.allAutoTags, ElementClass.HIDDEN);
      elementClassListAdd(this.allCustomTags, ElementClass.HIDDEN);
    } else {
      elementClassListRemove(this.allAutoTags, ElementClass.HIDDEN);
      elementClassListRemove(this.allCustomTags, ElementClass.HIDDEN);
    }
  }

  public getCheckedOptionsAutomatic(): SelectedOption[] {
    return this.filterOptions
      .filter(
        (option: ExtendedTagEnumListItem) =>
          option.selected && option.additionalData?.type === TagTypes.TAG_TYPE_AUTOMATIC
      )
      .map(option => {
        return {
          label: option.label,
          value: option.value,
        };
      });
  }

  public getCheckedOptionsCustom(): SelectedOption[] {
    return this.filterOptions
      .filter(
        (option: ExtendedTagEnumListItem) => option.selected && option.additionalData?.type === TagTypes.TAG_TYPE_CUSTOM
      )
      .map(option => {
        return {
          label: option.label,
          value: option.value,
        };
      });
  }

  /**
   * Overwritten
   */
  public updateSelectAllCheckboxesState() {
    this.getCheckedOptionsAutomatic().length > 0 &&
    this.getCheckedOptionsAutomatic().length ===
      this.filterOptions.filter(item => !item.disabled && item.additionalData?.type === TagTypes.TAG_TYPE_AUTOMATIC)
        .length
      ? this.setIsSelectAllAutoChecked(true)
      : this.setIsSelectAllAutoChecked(false);

    this.getCheckedOptionsCustom().length > 0 &&
    this.getCheckedOptionsCustom().length ===
      this.filterOptions.filter(item => !item.disabled && item.additionalData?.type === TagTypes.TAG_TYPE_CUSTOM).length
      ? this.setIsSelectAllCustomChecked(true)
      : this.setIsSelectAllCustomChecked(false);
  }

  protected renderListOptions() {
    return html`<div class="list--options" part="listOptions">
      ${this.renderSelectAllItemsCheckbox()} ${this.renderFilterOptionsList()} ${this.renderNoResultsTemplate()}
    </div>`;
  }

  protected renderNoResultsTemplate() {
    let noResultsString = this.noSearchResultsString;

    if (this.filterOptions.length === 0) {
      if (this.listIsFiltered) {
        noResultsString = this.noResultsInDefaultGroup;
      } else {
        noResultsString = this.emptyFilterString;
      }
    }

    return html` <div id="emptyTemplate" class="list--no-results">${noResultsString}</div> `;
  }

  isFilterOptionsEmpty(options): boolean {
    const optionsFiltered = options.filter((option: ExtendedTagEnumListItem) => {
      return option.additionalData?.type !== ExcludedTagTypes.NO_TAGS_SELECTED;
    });

    if (optionsFiltered.length > 0) {
      return false;
    }

    return true;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('filterOptions')) {
      this.updateEmptyTemplateDisplay(this.isFilterOptionsEmpty(this.filterOptions));
      this.setSelectedOptions(this.getSelectedValuesFromOptionsList());
      this.matchCheckboxesToSelectedOptions();

      if (this.canSelectAll) {
        this.updateSelectAllCheckboxesState();
      }
    }
  }
}
